import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/** @jsxImportSource react */
import { useEffect, useMemo, useRef, useState } from "react";
import { Box, Text } from "ink";
import { buildNamespaceTree, flattenTree, } from "../../NamespaceTree.js";
import { formatModeNote } from "../../ModeTag.js";
const getTaskKey = (item) => item.path.join("/");
export const buildProgressRows = (plan) => {
    const items = [
        ...Object.values(plan.resources),
        ...Object.values(plan.deletions).filter((item) => item !== undefined),
    ];
    const taskItems = [
        ...Object.values(plan.actions ?? {}),
        ...Object.values(plan.actionDeletions ?? {}),
    ].filter((t) => t !== undefined);
    const tree = buildNamespaceTree(items, taskItems);
    return flattenTree(tree)
        .filter((item) => item.type !== "binding")
        .map((item) => {
        if (item.type === "namespace") {
            return {
                key: getTaskKey(item),
                type: "namespace",
                id: item.id,
                depth: item.depth,
                action: item.action,
            };
        }
        if (item.type === "action") {
            return {
                key: getTaskKey(item),
                type: "task",
                id: item.id,
                depth: item.depth,
                actionType: item.actionType ?? "unknown",
                action: item.action,
            };
        }
        return {
            key: getTaskKey(item),
            type: "resource",
            id: item.id,
            depth: item.depth,
            resourceType: item.resourceType ?? "unknown",
            action: item.action,
            providerMode: item.providerMode,
            fromProviderMode: item.fromProviderMode,
            persistedApplyStatus: item.action === "noop"
                ? (() => {
                    const crud = findCrudByLogicalId(plan, item.id);
                    return crud?.action === "noop" ? crud.state.status : undefined;
                })()
                : undefined,
        };
    });
};
const buildLogicalIdIndex = (rows) => {
    const index = new Map();
    for (const row of rows) {
        if (row.type !== "resource" && row.type !== "task")
            continue;
        const keys = index.get(row.id);
        if (keys) {
            keys.push(row.key);
        }
        else {
            index.set(row.id, [row.key]);
        }
    }
    return index;
};
export function toPlanTask(rowOrId, planItem) {
    if (typeof rowOrId === "string") {
        return {
            key: rowOrId,
            id: rowOrId,
            type: planItem.resource.Type,
            status: planItem.action === "noop" ? planItem.state.status : "pending",
            updatedAt: Date.now(),
        };
    }
    return {
        key: rowOrId.key,
        id: rowOrId.id,
        type: rowOrId.resourceType,
        status: rowOrId.action === "noop"
            ? (rowOrId.persistedApplyStatus ?? "created")
            : "pending",
        updatedAt: Date.now(),
    };
}
const buildInitialTasks = (rows) => new Map(rows.flatMap((row) => row.type === "resource"
    ? [[row.key, toPlanTask(row)]]
    : row.type === "task"
        ? [
            [
                row.key,
                {
                    key: row.key,
                    id: row.id,
                    type: row.actionType,
                    // `noop` tasks are skipped — render as gray `•` from the start
                    // rather than briefly flashing the `ran` cyan styling.
                    status: row.action === "noop"
                        ? "skipped"
                        : "pending",
                    updatedAt: Date.now(),
                },
            ],
        ]
        : []));
export function PlanProgress(props) {
    const { source, plan } = props;
    const spinner = useGlobalSpinner();
    const rows = useMemo(() => buildProgressRows(plan), [plan]);
    const logicalIdIndex = useMemo(() => buildLogicalIdIndex(rows), [rows]);
    const [tasks, setTasks] = useState(() => buildInitialTasks(rows));
    const unsubscribeRef = useRef(null);
    useEffect(() => {
        unsubscribeRef.current?.();
        unsubscribeRef.current = source.subscribe((event) => {
            setTasks((prev) => {
                const next = new Map(prev);
                const keys = logicalIdIndex.get(event.id) ?? [];
                if (event.kind === "status-change") {
                    if (!event.bindingId) {
                        for (const key of keys) {
                            const current = next.get(key);
                            next.set(key, {
                                key,
                                id: event.id,
                                type: event.type,
                                status: event.status,
                                message: event.message ?? current?.message,
                                updatedAt: Date.now(),
                            });
                        }
                    }
                }
                else {
                    for (const key of keys) {
                        const current = next.get(key);
                        if (!current)
                            continue;
                        next.set(key, {
                            ...current,
                            message: event.message,
                            updatedAt: Date.now(),
                        });
                    }
                }
                return next;
            });
        });
        return () => {
            unsubscribeRef.current?.();
            unsubscribeRef.current = null;
        };
    }, [logicalIdIndex, source]);
    useEffect(() => {
        setTasks(buildInitialTasks(rows));
    }, [rows]);
    return (_jsx(Box, { flexDirection: "column", children: rows.map((row) => {
            const indent = "  ".repeat(row.depth);
            if (row.type === "namespace") {
                return (_jsxs(Box, { flexDirection: "row", children: [_jsx(Text, { children: indent }), _jsx(Box, { width: 2, children: _jsx(Text, { color: "blueBright", children: "\u21B3 " }) }), _jsx(Text, { color: "blueBright", children: row.id })] }, row.key));
            }
            if (row.type === "task") {
                const t = tasks.get(row.key);
                const status = t?.status ?? (row.action === "noop" ? "ran" : "pending");
                const color = statusColor(status);
                const icon = taskIcon(row.action, status, spinner);
                const label = row.action === "delete"
                    ? status === "deleted" || status === "retained"
                        ? status
                        : "drop"
                    : status === "ran"
                        ? row.action === "noop"
                            ? "skip"
                            : "ran"
                        : status === "running"
                            ? "running"
                            : status === "fail"
                                ? "fail"
                                : row.action === "noop"
                                    ? "skip"
                                    : "run";
                return (_jsxs(Box, { flexDirection: "column", children: [_jsxs(Box, { flexDirection: "row", children: [_jsx(Text, { children: indent }), _jsx(Box, { width: 2, children: _jsxs(Text, { color: color, children: [icon, " "] }) }), _jsx(Text, { bold: true, children: row.id }), _jsxs(Text, { dimColor: true, children: [" (", row.actionType, ")"] }), _jsxs(Text, { color: color, children: [" ", label] }), _jsxs(Text, { color: "cyan", dimColor: true, children: [" ", "[action]"] })] }), t?.message ? (_jsx(Box, { paddingLeft: row.depth * 2 + 2, children: _jsxs(Text, { dimColor: true, children: ["\u2022 ", t.message] }) })) : null] }, row.key));
            }
            const task = tasks.get(row.key) ?? toPlanTask(row);
            const displayStatus = getDisplayStatus(row, task.status);
            const color = statusColor(displayStatus);
            const icon = statusIcon(task.status, spinner);
            const modeNote = formatModeNote({
                mode: row.providerMode,
                priorMode: row.fromProviderMode,
                defaultMode: plan.defaultMode,
            });
            return (_jsxs(Box, { flexDirection: "column", children: [_jsxs(Box, { flexDirection: "row", children: [_jsx(Text, { children: indent }), _jsx(Box, { width: 2, children: _jsxs(Text, { color: color, children: [icon, " "] }) }), _jsx(Text, { bold: true, children: task.id }), _jsxs(Text, { dimColor: true, children: [" (", task.type, ")"] }), modeNote ? _jsxs(Text, { dimColor: true, children: [" (", modeNote, ")"] }) : null, _jsxs(Text, { color: color, children: [" ", displayStatus] })] }), task.message ? (_jsx(Box, { paddingLeft: row.depth * 2 + 2, children: _jsxs(Text, { dimColor: true, children: ["\u2022 ", task.message] }) })) : null] }, row.key));
        }) }));
}
function getDisplayStatus(row, status) {
    if (row.action === "noop" && (status === "created" || status === "updated")) {
        return "no change";
    }
    return status;
}
function statusColor(status) {
    switch (status) {
        case "no change":
            return "gray";
        case "pending":
            return "gray";
        case "creating":
        case "created":
            return "green";
        case "updating":
        case "updated":
            return "yellow";
        case "deleting":
        case "deleted":
            return "red";
        case "retained":
            return "gray";
        case "running":
        case "ran":
            return "cyan";
        case "skipped":
            return "gray";
        case "fail":
            return "redBright";
        default:
            return undefined;
    }
}
function taskIcon(action, status, spinnerChar) {
    if (status === "running")
        return spinnerChar;
    if (status === "fail")
        return "✗";
    if (status === "skipped")
        return "•";
    if (status === "ran")
        return action === "noop" ? "•" : "✓";
    if (status === "deleted" || status === "retained")
        return "✓";
    if (action === "delete")
        return "-";
    if (action === "noop")
        return "•";
    return "λ";
}
function statusIcon(status, spinnerChar) {
    if (isInProgress(status))
        return spinnerChar;
    if (status === "fail")
        return "✗";
    return "✓"; // created/updated/deleted/replaced/etc.
}
function isInProgress(status) {
    return (status === "pending" ||
        status === "creating" ||
        status === "updating" ||
        status === "deleting" ||
        status === "running");
}
const spinnerFrames = ["⠋", "⠙", "⠹", "⠸", "⠼", "⠴", "⠦", "⠧", "⠇", "⠏"];
function useGlobalSpinner(intervalMs = 80) {
    const [index, setIndex] = useState(0);
    useEffect(() => {
        const timer = setInterval(() => {
            setIndex((i) => (i + 1) % spinnerFrames.length);
        }, intervalMs);
        return () => clearInterval(timer);
    }, [intervalMs]);
    return spinnerFrames[index];
}
const findCrudByLogicalId = (plan, logicalId) => {
    for (const node of Object.values(plan.resources)) {
        if (node.resource.LogicalId === logicalId) {
            return node;
        }
    }
    for (const node of Object.values(plan.deletions)) {
        if (node?.resource.LogicalId === logicalId) {
            return node;
        }
    }
    return undefined;
};
//# sourceMappingURL=PlanProgress.js.map